import { useEffect, useRef, useState } from "react";
import { Sparkles, Send } from "lucide-react";
import api from "@/lib/api";
import { PortalHeader } from "@/pages/client/_shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const SUGGESTIONS = [
  "What should my next step be?",
  "How do I build business credit?",
  "What funding options fit my business?",
  "Help me prepare for formation.",
];

export default function ClientAdvisor() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    api.get("/portal/advisor/history")
      .then((r) => setMessages(r.data.map((m, i) => ({ ...m, mid: `h_${i}` }))))
      .catch(() => {});
  }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const ask = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setMessages((m) => [...m, { mid: `u_${Date.now()}_${m.length}`, role: "user", content: msg }]);
    setInput(""); setLoading(true);
    try {
      const { data } = await api.post("/portal/advisor/chat", { message: msg });
      setMessages((m) => [...m, { mid: `a_${Date.now()}_${m.length}`, role: "assistant", content: data.reply }]);
    } catch {
      setMessages((m) => [...m, { mid: `a_${Date.now()}_${m.length}`, role: "assistant", content: "Sorry, the AI Advisor is temporarily unavailable. Please try again." }]);
    } finally { setLoading(false); }
  };

  return (
    <div className="fv-fade-up flex flex-col h-[calc(100vh-8rem)]">
      <PortalHeader title="AI Advisor" subtitle="Personalized business guidance for your company, available 24/7." />

      <div className="flex-1 overflow-y-auto rounded-2xl border border-white/8 bg-[#0f0f11] p-5 space-y-4" data-testid="client-advisor-thread">
        {messages.length === 0 && !loading && (
          <div className="text-center py-8">
            <div className="w-14 h-14 rounded-2xl fv-gold-gradient flex items-center justify-center mx-auto mb-4"><Sparkles className="w-7 h-7 text-[#0A0A0B]" /></div>
            <p className="text-[#A1A1AA] mb-5">Ask me anything about launching, funding, or growing your business.</p>
            <div className="flex flex-wrap gap-2 justify-center max-w-lg mx-auto">
              {SUGGESTIONS.map((s) => (
                <button key={s} onClick={() => ask(s)} data-testid="client-advisor-suggestion"
                  className="text-xs px-3 py-2 rounded-full border border-white/10 text-[#D4D4D8] hover:border-[#D4AF37]/40 hover:text-white transition-colors">{s}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m) => (
          <div key={m.mid} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm whitespace-pre-wrap ${
              m.role === "user" ? "fv-gold-gradient text-[#0A0A0B]" : "bg-[#1C1C1F] text-[#D4D4D8] border border-white/8"}`}>{m.content}</div>
          </div>
        ))}
        {loading && <div className="flex justify-start"><div className="bg-[#1C1C1F] border border-white/8 rounded-2xl px-4 py-3 text-sm text-[#71717A]">Thinking…</div></div>}
        <div ref={endRef} />
      </div>

      <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="flex gap-2 mt-4" data-testid="client-advisor-form">
        <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask your AI Advisor…"
          data-testid="client-advisor-input" className="bg-[#121214] border-white/10 text-white h-11" />
        <Button type="submit" disabled={loading || !input.trim()} data-testid="client-advisor-send"
          className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-lg h-11 px-5 shrink-0"><Send className="w-4 h-4" /></Button>
      </form>
    </div>
  );
}
